import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CodeIcon from '@mui/icons-material/Code';
import moment from 'moment';
import { renderSuccessMessage } from 'src/lib/toastMessage';

const formatDateTime = (val) => {
  if (!val) return '—';
  const m = typeof val === 'number' ? (val < 1e11 ? moment.unix(val) : moment(val)) : moment(val);
  return m.isValid() ? m.format('DD MMM YYYY, hh:mm:ss A') : String(val);
};

export default function SmsAdminSettingDetailsModal({ open, onClose, setting }) {
  const [showRawJson, setShowRawJson] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!setting) return null;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    renderSuccessMessage('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const {
    seq_id,
    campaignIds = [],
    selectedUserId,
    requested_by,
    create_date,
    status,
    updateExistingRecords,
    D_required,
    S_required,
    F_required,
    P_required,
    DateTime_required,
    old_data = {},
  } = setting;

  const isPending = status === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            SMS Admin Setting #{seq_id ?? '—'}
          </Typography>
          <Chip
            size="small"
            icon={isPending ? <HourglassEmptyIcon sx={{ fontSize: 16 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            label={isPending ? 'Pending' : status === 1 ? 'In Progress' : status === 2 ? 'Processed' : 'Failed'}
            color={isPending ? 'warning' : status === 2 ? 'success' : 'info'}
            variant="soft"
            sx={{ fontWeight: 600 }}
          />
          {updateExistingRecords ? (
            <Chip size="small" label="Update Existing Records" color="primary" variant="soft" sx={{ fontWeight: 600 }} />
          ) : (
            <Chip size="small" label="Cut Status = 1 Only" variant="outlined" sx={{ fontWeight: 500 }} />
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {/* Meta info */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              User ID
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedUserId || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Requested By
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {requested_by || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Campaign IDs ({campaignIds.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {campaignIds.map((id) => (
                <Chip key={id} label={id} size="small" variant="outlined" />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Created Date
            </Typography>
            <Typography variant="body2">{formatDateTime(create_date)}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Count Actions */}
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
          SMS Count Modifications
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {D_required && (
            <Grid item xs={12} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>
                  Delivered (D)
                </Typography>
                <Typography variant="h6">
                  {D_required.count} {D_required.countMode === 'percent' ? '%' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Adjust from: {(D_required.adjustedTo || []).join(', ') || 'F'}
                </Typography>
              </Paper>
            </Grid>
          )}

          {S_required && (
            <Grid item xs={12} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700 }}>
                  Submitted (S)
                </Typography>
                <Typography variant="h6">
                  {S_required.count} {S_required.countMode === 'percent' ? '%' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Adjust from: {(S_required.adjustedTo || []).join(', ') || 'P'}
                </Typography>
              </Paper>
            </Grid>
          )}

          {F_required && (
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="error.main" sx={{ fontWeight: 700 }}>
                  Failed (F)
                </Typography>
                <Typography variant="h6">
                  {F_required.count} {F_required.countMode === 'percent' ? '%' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Adjust from: {(F_required.adjustedTo || []).join(', ') || 'D'}
                </Typography>
              </Paper>
            </Grid>
          )}

          {P_required && (
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700 }}>
                  Pending (P)
                </Typography>
                <Typography variant="h6">
                  {P_required.count} {P_required.countMode === 'percent' ? '%' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Adjust from: {(P_required.adjustedTo || []).join(', ') || 'D'}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Date Time Actions */}
        {DateTime_required && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
              Date & Time Modifications
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Campaign Start Time
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDateTime(DateTime_required.campaignDateTime)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Message Send Time
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDateTime(DateTime_required.messageSendTime)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Delivery Report Time
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDateTime(DateTime_required.deliveryTime)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {/* Old Data */}
        {old_data && Object.keys(old_data).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Original Snapshot Before Update
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.neutral' }}>
              <pre style={{ margin: 0, fontSize: '11px', overflowX: 'auto' }}>
                {JSON.stringify(old_data, null, 2)}
              </pre>
            </Paper>
          </>
        )}

        {/* Raw JSON toggle */}
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            startIcon={<CodeIcon />}
            onClick={() => setShowRawJson(!showRawJson)}
          >
            {showRawJson ? 'Hide Raw JSON' : 'Show Raw JSON'}
          </Button>
          <Collapse in={showRawJson}>
            <Box sx={{ position: 'relative', mt: 1 }}>
              <Tooltip title={copied ? 'Copied!' : 'Copy JSON'}>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(JSON.stringify(setting, null, 2))}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.neutral' }}>
                <pre style={{ margin: 0, fontSize: '11px', overflowX: 'auto' }}>
                  {JSON.stringify(setting, null, 2)}
                </pre>
              </Paper>
            </Box>
          </Collapse>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SmsAdminSettingDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setting: PropTypes.object,
};
